import React from "react";
import { useLocation } from "react-router-dom";
import config from "../../config/config";
import { FONT_FAMILY } from "../../config/typography";
import { useAuth } from "../login/AuthContext";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Stack,
  Container,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  ThemeProvider,
  createTheme,
  Backdrop
} from "@mui/material";
import {
  Print as PrintIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2C3E50",
    },
    secondary: {
      main: "#34495E",
    },
    success: {
      main: "#28a745",
    },
    info: {
      main: "#00A7B5",
    },
    background: {
      default: "#F8F9FA",
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
  },
  shape: {
    borderRadius: 8,
  },
});

function Certificate() {
  const { token } = useAuth();
  const location = useLocation();

  let {
    date,
    packageName,
    licenseNo,
    company,
    uid,
    startDate,
    endDate,
    ekModules,
    userCount,
    customerEmail,
    customerName,
    productFamily,
    serverType
  } = location.state || {};

  const [certificateImage, setCertificateImage] = React.useState(null);
  const [isLoadingCertificate, setIsLoadingCertificate] = React.useState(false);
  const [mailPreview, setMailPreview] = React.useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);
  const [fullscreenImage, setFullscreenImage] = React.useState(null);
  const [showSendModal, setShowSendModal] = React.useState(false);
  const [recipientEmail, setRecipientEmail] = React.useState("");
  const [recipientName, setRecipientName] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState("");

  React.useEffect(() => {
    if (customerEmail) {
      setRecipientEmail(customerEmail);
    }
    if (customerName || company) {
      setRecipientName(customerName || company);
    }
  }, [customerEmail, customerName, company]);

  // Sayfa yüklendiğinde sertifikayı oluştur
  React.useEffect(() => {
    generateCertificate();
  }, []);

  const generateCertificate = async () => {
    setIsLoadingCertificate(true);
    try {
      const response = await fetch(`${config.baseURL}/send-eposta/generate-certificate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: date || '',
          packageName: packageName || '',
          licenseNo: licenseNo || '',
          company: company || '',
          uid: uid || '',
          startDate: startDate || '',
          endDate: endDate || '',
          userCount: userCount ?? 0,
          serverType: serverType || 'Yerel Sunucu',
          ekModules: ekModules?.length
            ? ekModules.map((m) => m.ProgramAdi).filter(Boolean).join(", ")
            : "",
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setCertificateImage(imageUrl);
      } else {
        toast.error('Sertifika oluşturulamadı');
      }
    } catch (error) {
      console.error('Sertifika oluşturma hatası:', error);
      toast.error('Sertifika oluşturulurken hata oluştu');
    } finally {
      setIsLoadingCertificate(false);
    }
  };

  const handlePrint = () => {
    if (!certificateImage) {
      toast.error('Sertifika henüz yüklenmedi');
      return;
    }
    window.print();
  };

  const handleMailPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const response = await fetch(`${config.baseURL}/send-eposta/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: company || '',
          date: date || '',
          licenseNo: licenseNo || '',
          packageName: packageName || '',
          startDate: startDate || '',
          endDate: endDate || '',
          ekModules: ekModules?.length
            ? ekModules
                .filter(m => m.ProgramAdi !== "Dış Erişim")
                .map(m => m.ProgramAdi)
                .join(", ")
            : "",
          userCount: userCount || 0,
          uid: uid || '',
          serverType: serverType || 'Yerel Sunucu',
          productFamily: productFamily || 'pozitif',
        })
      });

      const data = await response.json();
      if (data.success && data.mailContent) {
        setMailPreview(data.mailContent);
      } else {
        toast.error('Mail önizlemesi yüklenemedi');
      }
    } catch (error) {
      console.error('Önizleme hatası:', error);
      toast.error('Önizleme yüklenirken hata oluştu');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSendMail = async () => {
    if (!recipientEmail) {
      toast.info('Lütfen alıcı email adresini girin');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      toast.info('Lütfen geçerli bir email adresi girin');
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${config.baseURL}/send-eposta/send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientEmail: recipientEmail,
          recipientName: recipientName || 'Müşterimiz',
          name: company || '',
          date: date || '',
          licenseNo: licenseNo || '',
          packageName: packageName || '',
          startDate: startDate || '',
          endDate: endDate || '',
          ekModules: ekModules?.length
            ? ekModules.map(m => m.ProgramAdi).join(", ")
            : "",
          userCount: userCount || 0,
          uid: uid || '',
          serverType: serverType || 'Yerel Sunucu',
          productFamily: productFamily || 'pozitif',
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`Mail başarıyla gönderildi!\n\nAlıcı: ${recipientEmail}\nFirma: ${company}\nMessage ID: ${data.messageId}`);
        setShowSuccessPopup(true);
        setShowSendModal(false);
      } else {
        toast.error(`Mail gönderilemedi: ${data.error}`);
      }
    } catch (error) {
      console.error('Mail gönderme hatası:', error);
      toast.error('Mail gönderirken hata oluştu');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="yazialani-kutu"
        sx={{
          height: "100vh",
          overflowY: "auto",
          minHeight: "100vh",
          bgcolor: "background.default",
          py: 4,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Container maxWidth="lg" sx={{ width: "100%" }}>
          {/* Butonlar */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mb: 3 }}
            className="no-print"
          >
            <Button
              variant="contained"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={!certificateImage || isLoadingCertificate}
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: "none",
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                }
              }}
            >
              Sertifikayı Yazdır
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={isLoadingCertificate ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={generateCertificate}
              disabled={isLoadingCertificate}
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: "none",
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                }
              }}
            >
              {isLoadingCertificate ? "Yükleniyor..." : "Yenile"}
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={isLoadingPreview ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
              onClick={handleMailPreview}
              disabled={isLoadingPreview}
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: "none",
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                }
              }}
            >
              {isLoadingPreview ? "Yükleniyor..." : "Mail Önizle"}
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => setShowSendModal(true)}
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: "none",
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                }
              }}
            >
              Mail Gönder
            </Button>
          </Stack>

          {/* Mail Gönderme Modal */}
          <Dialog
            open={showSendModal}
            onClose={() => setShowSendModal(false)}
            maxWidth="sm"
            fullWidth
            className="no-print"
            PaperProps={{
              sx: {
                borderRadius: 2,
              }
            }}
          >
            <DialogTitle sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1
            }}>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon color="primary" /> Mail Gönder
              </Typography>
              <IconButton
                onClick={() => setShowSendModal(false)}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                label="Alıcı Email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="ornek@firma.com"
                required
                sx={{ my: 3 }}
                error={recipientEmail !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)}
                helperText={recipientEmail !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail) ? "Geçerli bir email adresi girin" : ""}
              />
              <TextField
                fullWidth
                label="Alıcı Adı (Opsiyonel)"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Müşteri Adı"
                sx={{ mb: 3 }}
              />
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "grey.50",
                  p: 2,
                  borderRadius: 1,
                  borderLeft: "4px solid",
                  borderColor: "primary.main"
                }}
              >
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Gönderilecek içerik:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 3, color: "text.secondary" }}>
                  <li>Lisans Bilgileri Görseli</li>
                  <li>Sertifika Görseli</li>
                  <li>Kullanıcı Sözleşmeleri (2 adet)</li>
                </Box>
              </Paper>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setShowSendModal(false)}
                color="inherit"
                sx={{ textTransform: "none" }}
              >
                İptal
              </Button>
              <Button
                onClick={handleSendMail}
                disabled={isSending || !recipientEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)}
                variant="contained"
                color="primary"
                startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                sx={{ textTransform: "none" }}
              >
                {isSending ? "Gönderiliyor..." : "Gönder"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Başarı Pop-up */}
          <Dialog
            open={showSuccessPopup}
            onClose={() => setShowSuccessPopup(false)}
            maxWidth="sm"
            fullWidth
            className="no-print"
            PaperProps={{
              sx: {
                borderRadius: 2,
                textAlign: "center",
                p: 2
              }
            }}
          >
            <DialogContent sx={{ pt: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 50, color: "white" }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight={700} color="success.main">
                Mail Başarıyla Gönderildi!
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.6,
                  mb: 2
                }}
              >
                {successMessage}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <Button
                onClick={() => setShowSuccessPopup(false)}
                variant="contained"
                color="success"
                sx={{
                  textTransform: "none",
                  px: 4,
                  py: 1
                }}
              >
                Tamam
              </Button>
            </DialogActions>
          </Dialog>

          {/* Fullscreen Image Modal */}
          <Backdrop
            open={!!fullscreenImage}
            onClick={() => setFullscreenImage(null)}
            sx={{
              zIndex: (theme) => theme.zIndex.modal + 1,
              bgcolor: "rgba(0, 0, 0, 0.95)",
              cursor: "zoom-out"
            }}
            className="no-print"
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setFullscreenImage(null);
              }}
              sx={{
                position: "absolute",
                top: 20,
                right: 20,
                bgcolor: "error.main",
                color: "white",
                "&:hover": {
                  bgcolor: "error.dark",
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={fullscreenImage}
              alt="Tam Ekran"
              onClick={(e) => e.stopPropagation()}
              sx={{
                maxWidth: "95%",
                maxHeight: "95%",
                objectFit: "contain",
                borderRadius: 2,
                boxShadow: 8,
                cursor: "default"
              }}
            />
          </Backdrop>

          {/* Mail Önizleme */}
          {mailPreview && (
            <Card
              className="no-print"
              sx={{
                mb: 3,
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 3,
                  borderBottom: 1,
                  borderColor: "divider",
                  flexShrink: 0
                }}
              >
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <EmailIcon color="primary" /> Mail Önizlemesi
                </Typography>
                <IconButton
                  onClick={() => setMailPreview(null)}
                  color="error"
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  p: 3,
                  overflowY: "auto",
                  flex: 1
                }}
              >
                {mailPreview.text && (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: "grey.50",
                      borderLeft: "4px solid",
                      borderColor: "success.main"
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DescriptionIcon fontSize="small" /> Mail İçeriği:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.6
                      }}
                    >
                      {mailPreview.text}
                    </Typography>
                  </Paper>
                )}

                {mailPreview.images && mailPreview.images.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <ImageIcon fontSize="small" /> Görsel Ekleri
                      <Chip label={mailPreview.images.length} size="small" color="primary" />
                    </Typography>
                    <Grid container spacing={2}>
                      {mailPreview.images.map((image, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Card
                            sx={{
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                boxShadow: 4,
                                transform: "scale(1.02)"
                              }
                            }}
                            onClick={() => setFullscreenImage(image.data)}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                bgcolor: "grey.50",
                                borderBottom: 1,
                                borderColor: "divider",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                              }}
                            >
                              <Typography variant="caption" fontWeight={500}>
                                {image.name}
                              </Typography>
                              <Chip
                                label="Tam Ekran"
                                size="small"
                                icon={<ZoomInIcon />}
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                            <Box
                              component="img"
                              src={image.data}
                              alt={image.name}
                              sx={{
                                width: "100%",
                                height: "auto",
                                display: "block"
                              }}
                            />
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {mailPreview.attachments && mailPreview.attachments.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <DescriptionIcon fontSize="small" /> Doküman Ekleri
                      <Chip label={mailPreview.attachments.length} size="small" color="primary" />
                    </Typography>
                    <Stack spacing={1.5}>
                      {mailPreview.attachments.map((attachment, index) => (
                        <Paper
                          key={index}
                          component="a"
                          href={`${config.baseURL}/download/${attachment.path}`}
                          download={attachment.name}
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: "grey.50",
                            border: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            textDecoration: "none",
                            transition: "all 0.2s",
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: "grey.100",
                              borderColor: "primary.main"
                            }
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="body2" fontWeight={600}>
                              {attachment.name}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<DownloadIcon />}
                            sx={{ textTransform: "none" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            İndir
                          </Button>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Card>
          )}

          {/* Sertifika Görseli */}
          <Card
            className="certificate-card"
            sx={{
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "21cm",
              bgcolor: "#ffffff",
              maxWidth: "29.7cm",

            }}
          >
            {isLoadingCertificate ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 3, color: "text.secondary" }}>
                  Sertifika oluşturuluyor...
                </Typography>
              </Box>
            ) : certificateImage ? (
              <Box
                component="img"
                src={certificateImage}
                alt="Sertifika"
                className="certificate-print"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  maxWidth: "29.7cm"
                }}
              />
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="error">
                  Sertifika yüklenemedi
                </Typography>
                <Button
                  variant="contained"
                  onClick={generateCertificate}
                  sx={{ mt: 2 }}
                  startIcon={<RefreshIcon />}
                >
                  Tekrar Dene
                </Button>
              </Box>
            )}
          </Card>
        </Container>
      </Box>

      {/* Yazdırma Stilleri */}
      <style>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          @page {
            size: A4 landscape;
            margin: 0;
          }

          .side-bar {
            display: none !important;
          }

          html, body {
            width: 29.7cm;
            height: 21cm;
            margin: 0;
            padding: 0;
          }

          .no-print {
            display: none !important;
          }

          .yazialani-kutu, .yazialani-kutu > .MuiContainer-root {
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
          }

          .certificate-card {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            page-break-after: avoid !important;
          }

          .certificate-print {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            width: 29.7cm !important;
            height: 21cm !important;
            max-width: none !important;
          }
        }
      `}</style>
    </ThemeProvider>
  );
}

export default Certificate;